import {
    HostedInstrument,
    InvalidArgumentError,
    isHostedInstrumentLike,
    isVaultedInstrument,
    OrderFinalizationNotRequiredError,
    OrderPaymentRequestBody,
    OrderRequestBody,
    Payment,
    PaymentArgumentInvalidError,
    PaymentInitializeOptions,
    PaymentInstrumentPayload,
    PaymentIntegrationService,
    PaymentMethodInvalidError,
    PaymentRequestOptions,
    PaymentStrategy,
    VaultedInstrument,
} from '@bigcommerce/checkout-sdk/payment-integration-api';
import {
    getPaypalMessagesStylesFromBNPLConfig,
    isPaypalCommerceProviderError,
    isRedirectActionError,
    MessagingOptions,
    PayPalBNPLConfigurationItem,
    PayPalCommerceSdk,
    PayPalMessagesSdk,
} from '@bigcommerce/checkout-sdk/paypal-commerce-utils';
import { LoadingIndicator } from '@bigcommerce/checkout-sdk/ui';
import { isBaseInstrument } from '@bigcommerce/checkout-sdk/utility';

import PayPalCommerceIntegrationService from '../paypal-commerce-integration-service';
import {
    ApproveCallbackPayload,
    ClickCallbackActions,
    PayPalCommerceButtons,
    PayPalCommerceButtonsOptions,
    PayPalCommerceInitializationData,
} from '../paypal-commerce-types';

import PayPalCommercePaymentInitializeOptions, {
    WithPayPalCommercePaymentInitializeOptions,
} from './paypal-commerce-payment-initialize-options';

export default class PayPalCommercePaymentStrategy implements PaymentStrategy {
    private loadingIndicatorContainer?: string;
    private orderId?: string;
    private paypalButton?: PayPalCommerceButtons;
    private paypalcommerce?: PayPalCommercePaymentInitializeOptions;

    constructor(
        private paymentIntegrationService: PaymentIntegrationService,
        private paypalCommerceIntegrationService: PayPalCommerceIntegrationService,
        private paypalCommerceSdk: PayPalCommerceSdk,
        private loadingIndicator: LoadingIndicator,
    ) {}

    async initialize(
        options?: PaymentInitializeOptions & WithPayPalCommercePaymentInitializeOptions,
    ): Promise<void> {
        const { methodId, paypalcommerce } = options || {};

        this.paypalcommerce = paypalcommerce;

        if (!methodId) {
            throw new InvalidArgumentError(
                'Unable to initialize payment because "options.methodId" argument is not provided.',
            );
        }

        if (!paypalcommerce) {
            throw new InvalidArgumentError(
                `Unable to initialize payment because "options.paypalcommerce" argument is not provided.`,
            );
        }

        await this.paymentIntegrationService.loadPaymentMethod(methodId);

        const state = this.paymentIntegrationService.getState();
        const paymentMethod =
            state.getPaymentMethodOrThrow<PayPalCommerceInitializationData>(methodId);

        const { container, bannerContainerId } = paypalcommerce;

        const {
            orderId,
            paypalBNPLConfiguration = [],
            isPayPalCreditAvailable,
        } = paymentMethod.initializationData || {};

        if (bannerContainerId !== undefined) {
            if (!document.getElementById(bannerContainerId)) {
                // eslint-disable-next-line no-console
                console.error('Unable to create banner without valid banner container ID.');

                return;
            }

            const bannerConfiguration = paypalBNPLConfiguration?.find(
                ({ id }) => id === 'checkout',
            );

            if (isPayPalCreditAvailable || !bannerConfiguration?.status) {
                return;
            }

            const paypalMessages = await this.paypalCommerceSdk.getPayPalMessages(
                paymentMethod,
                state.getCartOrThrow().currency.code,
            );

            if (!paypalMessages || typeof paypalMessages?.Messages !== 'function') {
                console.error(
                    '[BC PayPalCommerce PayLater]: banner could not be rendered, due to issues with loading PayPal SDK',
                );

                return;
            }

            return this.renderMessages(paypalMessages, bannerContainerId, bannerConfiguration);
        }

        // Info:
        // The PayPal button and fields should not be rendered when shopper was redirected to Checkout page
        // after using smart payment button on PDP or Cart page. In this case backend returns order id if
        // it is available in checkout session. Therefore, it is not necessary to render PayPal button.
        if (orderId) {
            this.orderId = paymentMethod.initializationData?.orderId;

            return;
        }

        this.loadingIndicatorContainer = container?.split('#')[1];

        await this.paypalCommerceIntegrationService.loadPayPalSdk(methodId);

        if (paypalcommerce.onInit && typeof paypalcommerce.onInit === 'function') {
            paypalcommerce.onInit(() => this.renderButton(methodId, paypalcommerce));
        }

        if (
            paypalcommerce.shouldRenderPayPalButtonOnInitialization === undefined ||
            paypalcommerce.shouldRenderPayPalButtonOnInitialization
        ) {
            this.renderButton(methodId, paypalcommerce);
        }
    }

    async execute(payload: OrderRequestBody, options?: PaymentRequestOptions): Promise<void> {
        const { payment, ...order } = payload;
        const { onError } = this.paypalcommerce || {};

        if (!payment) {
            throw new PaymentArgumentInvalidError(['payment']);
        }

        const { methodId, paymentData } = payment;

        if (this.isPayPalVaultedInstrumentPaymentData(paymentData) && !this.orderId) {
            this.orderId = await this.createOrder();
        }

        if (!this.orderId) {
            throw new PaymentMethodInvalidError();
        }

        const paymentPayload = this.isPayPalVaultedInstrumentPaymentData(paymentData)
            ? this.prepareVaultedInstrumentPaymentPayload(methodId, this.orderId, paymentData)
            : this.preparePaymentPayload(methodId, this.orderId, paymentData);

        try {
            await this.paymentIntegrationService.submitOrder(order, options);
            await this.paymentIntegrationService.submitPayment(paymentPayload);
        } catch (error: unknown) {
            if (isRedirectActionError(error)) {
                const redirectUrl = error.body.additional_action_required?.data?.redirect_url;

                return new Promise(() => window.location.replace(redirectUrl));
            }

            if (this.isProviderError(error)) {
                await this.paypalCommerceIntegrationService.loadPayPalSdk(payment.methodId);

                await new Promise((_resolve, reject) => {
                    if (this.paypalcommerce) {
                        this.paypalButton?.close();
                        this.renderButton(payment.methodId, this.paypalcommerce);
                        this.handleError(new Error('INSTRUMENT_DECLINED'), onError);
                    }

                    reject();
                });
            }

            this.handleError(error, onError);

            return Promise.reject();
        }
    }

    finalize(): Promise<void> {
        return Promise.reject(new OrderFinalizationNotRequiredError());
    }

    deinitialize(): Promise<void> {
        this.orderId = undefined;

        this.paypalButton?.close();

        return Promise.resolve();
    }

    private prepareVaultedInstrumentPaymentPayload(
        methodId: string,
        paypalOrderId: string,
        paymentData: VaultedInstrument & HostedInstrument,
    ): Payment {
        const { instrumentId, shouldSetAsDefaultInstrument } = paymentData;

        const shouldConfirmInstrument = !this.isTrustedVaultingFlow(paymentData);

        if (shouldConfirmInstrument) {
            return {
                methodId,
                paymentData: {
                    shouldSaveInstrument: shouldConfirmInstrument,
                    shouldSetAsDefaultInstrument,
                    formattedPayload: {
                        paypal_account: {
                            order_id: paypalOrderId,
                        },
                    },
                },
            };
        }

        return {
            methodId,
            paymentData: {
                instrumentId,
                shouldSetAsDefaultInstrument,
                formattedPayload: {
                    paypal_account: {
                        order_id: paypalOrderId,
                    },
                    bigpay_token: {
                        token: instrumentId,
                    },
                },
            },
        };
    }

    private preparePaymentPayload(
        methodId: string,
        paypalOrderId: string,
        paymentData: OrderPaymentRequestBody['paymentData'],
    ): Payment {
        const { shouldSaveInstrument = false, shouldSetAsDefaultInstrument = false } =
            isHostedInstrumentLike(paymentData) ? paymentData : {};

        return {
            methodId,
            paymentData: {
                shouldSaveInstrument,
                shouldSetAsDefaultInstrument,
                formattedPayload: {
                    paypal_account: {
                        order_id: paypalOrderId,
                    },
                },
            },
        };
    }

    /**
     *
     * Button methods/callbacks
     *
     * */
    private renderButton(
        methodId: string,
        paypalcommerce: PayPalCommercePaymentInitializeOptions,
    ): void {
        const paypalSdk = this.paypalCommerceIntegrationService.getPayPalSdkOrThrow();

        const state = this.paymentIntegrationService.getState();
        const paymentMethod =
            state.getPaymentMethodOrThrow<PayPalCommerceInitializationData>(methodId);
        const { paymentButtonStyles } = paymentMethod.initializationData || {};
        const { checkoutPaymentButtonStyles } = paymentButtonStyles || {};
        const { container, onError, onRenderButton, onValidate, submitForm } = paypalcommerce;

        if (!container) {
            throw new InvalidArgumentError(
                'Unable to initialize payment because "container" argument is not provided.',
            );
        }

        const buttonOptions: PayPalCommerceButtonsOptions = {
            ...(this.isPaypalCommerceAppSwitchEnabled(methodId) && {
                appSwitchWhenAvailable: true,
            }),
            fundingSource: paypalSdk.FUNDING.PAYPAL,
            style: this.paypalCommerceIntegrationService.getValidButtonStyle(
                checkoutPaymentButtonStyles,
            ),
            createOrder: () => this.createOrder(),
            onClick: (_, actions) => this.handleClick(actions, onValidate),
            onApprove: (data) => this.handleApprove(data, submitForm),
            onError: (error) => this.handleError(error, onError),
            onCancel: () => this.toggleLoadingIndicator(false),
        };

        this.paypalButton = paypalSdk.Buttons(buttonOptions);

        if (!this.paypalButton.isEligible()) {
            return;
        }

        if (onRenderButton && typeof onRenderButton === 'function') {
            onRenderButton();
        }

        if (this.paypalButton.hasReturned?.() && this.isPaypalCommerceAppSwitchEnabled(methodId)) {
            this.paypalButton.resume?.();
        } else {
            this.paypalButton.render(container);
        }
    }

    private async handleClick(
        actions: ClickCallbackActions,
        onValidate: PayPalCommercePaymentInitializeOptions['onValidate'],
    ): Promise<void> {
        const { resolve, reject } = actions;

        const onValidationPassed = () => {
            this.toggleLoadingIndicator(true);

            return resolve();
        };

        return onValidate?.(onValidationPassed, reject);
    }

    private handleApprove(
        { orderID }: ApproveCallbackPayload,
        submitForm: PayPalCommercePaymentInitializeOptions['submitForm'],
    ): void {
        this.orderId = orderID;

        submitForm?.();
    }

    private handleError(
        error: unknown,
        onError: PayPalCommercePaymentInitializeOptions['onError'],
    ): void {
        this.toggleLoadingIndicator(false);

        if (onError && typeof onError === 'function') {
            onError(error);
        }
    }

    private async createOrder(): Promise<string> {
        const fieldsValues = this.getFieldsValues();

        return this.paypalCommerceIntegrationService.createOrder('paypalcommercecheckout', {
            shouldSaveInstrument: fieldsValues?.shouldSaveInstrument || false,
        });
    }

    /**
     *
     * Vaulting flow methods
     *
     * */
    private getFieldsValues(): HostedInstrument | undefined {
        const { getFieldsValues } = this.paypalcommerce || {};

        return typeof getFieldsValues === 'function' ? getFieldsValues() : undefined;
    }

    private isTrustedVaultingFlow(paymentData?: PaymentInstrumentPayload): boolean {
        if (paymentData && isVaultedInstrument(paymentData)) {
            const state = this.paymentIntegrationService.getState();

            const instruments = state.getInstruments();
            const findInstrument = instruments?.find(
                (instrument) =>
                    isBaseInstrument(instrument) &&
                    instrument.bigpayToken === paymentData.instrumentId,
            );
            const trustedShippingAddress = isBaseInstrument(findInstrument)
                ? findInstrument.trustedShippingAddress
                : {};

            return !!trustedShippingAddress;
        }

        return false;
    }

    /**
     *
     * Loading Indicator methods
     *
     * */
    private toggleLoadingIndicator(isLoading: boolean): void {
        if (isLoading && this.loadingIndicatorContainer) {
            this.loadingIndicator.show(this.loadingIndicatorContainer);
        } else {
            this.loadingIndicator.hide();
        }
    }

    /**
     *
     * Guards
     *
     */
    private isPayPalVaultedInstrumentPaymentData(
        paymentData?: PaymentInstrumentPayload,
    ): paymentData is VaultedInstrument & HostedInstrument {
        return (
            !!paymentData && isVaultedInstrument(paymentData) && isHostedInstrumentLike(paymentData)
        );
    }

    /**
     *
     * Render Pay Later Messages
     *
     * */
    private renderMessages(
        paypalMessages: PayPalMessagesSdk,
        bannerContainerId: string,
        bannerConfiguration: PayPalBNPLConfigurationItem,
    ): void {
        const checkout = this.paymentIntegrationService.getState().getCheckoutOrThrow();

        const paypalMessagesOptions: MessagingOptions = {
            amount: checkout.outstandingBalance,
            placement: 'payment',
            style: getPaypalMessagesStylesFromBNPLConfig(bannerConfiguration),
        };

        paypalMessages.Messages(paypalMessagesOptions).render(`#${bannerContainerId}`);
    }

    /**
     *
     * Error handling
     *
     */
    private isProviderError(error: unknown): boolean {
        if (isPaypalCommerceProviderError(error)) {
            const paypalProviderError = error?.errors?.filter((e) => e.provider_error) || [];

            return paypalProviderError[0]?.provider_error?.code === 'INSTRUMENT_DECLINED';
        }

        return false;
    }

    /**
     *
     * PayPal AppSwitch enabling handling
     *
     */
    private isPaypalCommerceAppSwitchEnabled(methodId: string): boolean {
        const state = this.paymentIntegrationService.getState();
        const paymentMethod =
            state.getPaymentMethodOrThrow<PayPalCommerceInitializationData>(methodId);

        return paymentMethod.initializationData?.isAppSwitchEnabled || false;
    }
}
